import { useEffect, useRef, useState } from "react";
import { fmtDate } from "../../extensions/utils";
import { isMobile } from "../../extensions/design";
import SportTooltip from "../../controls/sport-tooltip/sport-tooltip";
import { serverFetch } from "@/server";


// Имя клиента в окне визита со стрелкой переключения участника группы.
// Стрелка стоит сразу после имени и раскрывает список группы.
// rootId - представитель группы (корень группы), остальные - его дети.
const VisitClientSelector = ({ clients, currentId, rootId, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef(null);
  // ID клиентов группы, у которых уже есть открытый визит (выбор недоступен)
  const [openVisitClientIds, setOpenVisitClientIds] = useState(new Set());

  const current = clients.find((c) => c.id === currentId) || clients[0];

  // Проверяем, у каких участников группы уже есть открытый визит - таких нельзя выбирать
  useEffect(() => {
    const ids = new Set();
    const counter = { pending: 0 };
    const clientsToCheck = clients.filter(c => c && c.id !== currentId);
    const checkDone = () => {
      if (--counter.pending <= 0) setOpenVisitClientIds(ids);
    };
    clientsToCheck.forEach(client => {
      counter.pending++;
      serverFetch(`visit/Client/${client.id}?isOpen=true`)
        .then(visits => {
          const openVisit = Array.isArray(visits) ? visits.find(v => !v.finish) : null;
          if (openVisit) ids.add(client.id);
          checkDone();
        })
        .catch(() => checkDone());
    });
    if (counter.pending === 0) setOpenVisitClientIds(ids);
  }, [clients, currentId]);

  // Закрытие по клику вне компонента и по Esc
  useEffect(() => {
    if (!open) return;

    const onDocClick = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const norm = (v) => String(v ?? "").toLowerCase();
  const term = search.trim().toLowerCase();
  const filtered = clients.filter((c) => {
    if (!term) return true;
    return [c.name, c.phone, c.card].map(norm).some((v) => v.includes(term));
  });

  const initials = (name) => (name ? String(name).charAt(0).toUpperCase() : "?");

  // Подсказка по наведению на стрелку: назначение переключателя
  const toggleTooltip = "Выбор клиента из группы для открытия визита";

  return (
    <div className={`client-selector ${isMobile ? "client-selector_mobile" : ""}`} ref={rootRef}>
      <div className="client-selector-row">
        <span className="client-selector-name" title={current?.name}>{current?.name}</span>
        <SportTooltip title={toggleTooltip} placement="bottom">
          <button
            type="button"
            className={`client-selector-toggle ${open ? "client-selector-toggle_open" : ""}`}
            onClick={() => setOpen((prev) => !prev)}
            aria-label="Выбрать участника группы"
            aria-expanded={open}
          >
            <span className="client-selector-toggle-icon" aria-hidden>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>
        </SportTooltip>
      </div>

      {open && (
        <div className="client-selector-dropdown">
          <input
            className="dropdown-search"
            type="text"
            placeholder="Поиск..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className="dropdown-list">
            {filtered.map((client) => {
              const isCurrent = client.id === currentId;
              const isDisabled = openVisitClientIds.has(client.id);
              const role = (client.id === rootId) ? "Представитель" : null;
              return (
                <div
                  key={client.id}
                  className={`dropdown-option ${isCurrent ? "dropdown-option_current" : ""} ${isDisabled ? "dropdown-option_disabled" : ""}`}
                  onClick={() => {
                    if (isCurrent || isDisabled) {
                      setOpen(false);
                      return;
                    }
                    onChange(client.id);
                    setOpen(false);
                  }}
                >
                  <div className="dropdown-avatar">
                    {client?.imageId ? (
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}/api/image/${client.imageId}.jpg`}
                        alt={client.name}
                      />
                    ) : (
                      <span>{initials(client.name)}</span>
                    )}
                  </div>
                  <div className="dropdown-info">
                    <div className="dropdown-name">
                      {client.name}
                      {role && <span className={`dropdown-role ${role === "Я" ? "dropdown-role_self" : ""}`}>{role}</span>}
                      {isDisabled && <span className="dropdown-role_disabled">уже открыт</span>}
                    </div>
                    <div className="dropdown-meta">
                      {client.phone && <span>{client.phone}</span>}
                      {client.dateBirth && <span>{fmtDate(client.dateBirth)}</span>}
                    </div>
                  </div>
                  {isCurrent && (
                    <span className="dropdown-check">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </span>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && <div className="dropdown-empty">Ничего не найдено</div>}
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitClientSelector;
